import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useCurrency } from '../context/CurrencyContext';
import api, { getImageUrl } from '../api/api';
import './Orders.css';

function Orders() {
  const { user } = useUser();
  const currency = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const query = user.mobile ? `mobile=${user.mobile}` : `email=${user.email}`;
      const { data } = await api.get(`/user/orders?${query}`);
      setOrders(data);
    } catch (e) {
      setError('Failed to load orders. Please try again.');
      console.error(e);
    }
    setLoading(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f57c00',
      processing: '#1565c0',
      shipped: '#2e7d32',
      delivered: '#1b5e20',
      cancelled: '#c62828',
      refunded: '#6a1b9a',
      return: '#d84315',
      reshipment: '#0277bd'
    };
    return colors[status] || '#666';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      processing: '📦',
      shipped: '🚚',
      delivered: '✅',
      cancelled: '❌',
      refunded: '💰',
      return: '↩️',
      reshipment: '🔄'
    };
    return icons[status] || '📋';
  };

  if (!user) {
    return (
      <div className="orders-page section">
        <div className="container">
          <div className="orders-empty">
            <div className="orders-empty-icon">🔒</div>
            <h2>Sign In Required</h2>
            <p>Please sign in to view your orders.</p>
            <Link to="/cart" className="btn">Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="orders-page section">
        <div className="container">
          <div className="orders-loading">
            <div className="spinner"></div>
            <p>Loading your orders...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-page section">
        <div className="container">
          <div className="orders-error">
            <p>{error}</p>
            <button className="btn" onClick={loadOrders}>Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="orders-page section">
        <div className="container">
          <div className="orders-empty">
            <div className="orders-empty-icon">📦</div>
            <h2>No Orders Yet</h2>
            <p>You haven't placed any orders yet.</p>
            <Link to="/products" className="btn">Start Shopping</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page section">
      <div className="container">
        <div className="orders-header">
          <h1>My Orders</h1>
          <p className="orders-user-info">
            👤 {user.name} · {user.mobile || user.email}
          </p>
        </div>

        <div className="orders-list">
          {orders.map(order => (
            <div key={order._id} className="order-card">
              {/* Order Header */}
              <div className="order-header">
                <div className="order-id-section">
                  <span className="order-label">Order ID</span>
                  <span className="order-id">{order.orderID.substring(0, 16).toUpperCase()}</span>
                </div>
                <div className="order-date-section">
                  <span className="order-label">Placed on</span>
                  <span className="order-date">
                    {new Date(order.createdAt).toLocaleDateString('en-IE', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="order-total-section">
                  <span className="order-label">Total</span>
                  <span className="order-total">{order.currency} {order.amount}</span>
                </div>
              </div>

              {/* Order Status */}
              <div className="order-status-bar">
                <div className="order-status-badge" style={{ background: getStatusColor(order.status) + '20', color: getStatusColor(order.status) }}>
                  <span className="status-icon">{getStatusIcon(order.status)}</span>
                  <span className="status-text">{order.status.charAt(0).toUpperCase() + order.status.slice(1)}</span>
                </div>
                <div className="order-gateway">
                  <span className="gateway-icon">{order.gateway === 'stripe' ? '💳' : '🅿️'}</span>
                  <span>{order.gateway === 'stripe' ? 'Card Payment' : 'PayPal'}</span>
                </div>
              </div>

              {/* Order Items */}
              <div className="order-items">
                {(order.items || []).map((item, i) => (
                  <div key={i} className="order-item">
                    <div className="order-item-image">
                      {item.image && (
                        <img 
                          src={getImageUrl(item.image)} 
                          alt={item.name}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </div>
                    <div className="order-item-details">
                      <h4>{item.name}</h4>
                      <p className="order-item-qty">Quantity: {item.qty}</p>
                    </div>
                    <div className="order-item-price">
                      {currency}{(item.price * item.qty).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Delivery Address */}
              {order.customer?.address?.line1 && (
                <div className="order-delivery">
                  <span className="delivery-icon">📍</span>
                  <div className="delivery-address">
                    <strong>Delivery Address:</strong>
                    <p>
                      {order.customer.address.line1}, {order.customer.address.city}
                      {order.customer.address.eircode && `, ${order.customer.address.eircode}`}
                      {order.customer.address.country && `, ${order.customer.address.country}`}
                    </p>
                  </div>
                </div>
              )}

              {/* Estimated Delivery */}
              <div className="order-notes">
                <span className="notes-icon">🚚</span>
                <div className="notes-content">
                  <strong>Expected Delivery:</strong>
                  <p>
                    {order.estimatedDelivery
                      ? new Date(order.estimatedDelivery).toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' })
                      : 'Delivery date will be updated soon'}
                  </p>
                </div>
              </div>

              {/* Order Notes */}
              {order.notes && (
                <div className="order-notes">
                  <span className="notes-icon">📝</span>
                  <div className="notes-content">
                    <strong>Note:</strong>
                    <p>{order.notes}</p>
                  </div>
                </div>
              )}

              {/* Order Actions */}
              <div className="order-actions">
                <a href={`tel:0871002241`} className="btn-small">
                  📞 Contact Us
                </a>
                {order.status === 'delivered' && (
                  <Link to="/products" className="btn-small">
                    🛒 Order Again
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Orders;
